import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { AgentBackLink } from "@/pages/agent/components/agent-back-link";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentProjectDetailCard } from "@/pages/agent/project/agent-project-detail-card";
import { AgentProjectMetaCard } from "@/pages/agent/project/agent-project-meta-card";
import { buildProjectDetailViewModel } from "@/pages/agent/project/agent-project-page.utils";
import { showNotImplementedAlert } from "@/pages/agent/utils/agent-not-implemented";
import { useAppStore } from "@/store";
import type { AssistantProjectObservable } from "@/store/assistant";

interface AgentProjectPageProps {
  projectId: string;
}

export const AgentProjectPage = observer(({ projectId }: AgentProjectPageProps) => {
  const { store } = useAppStore();
  const project = store.assistantProjects.getAssistantProjectObservableById({
    assistantProjectId: projectId,
  });

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Projects}>
      <AgentBackLink
        label="Back to Projects"
        onClick={() => {
          store.agentOverlay.openProjects();
        }}
      />

      {project ? (
        <ProjectDetail project={project} />
      ) : (
        <StyledNotFound color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Project not found.
        </StyledNotFound>
      )}
    </AgentPageLayout>
  );
});

const ProjectDetail = observer(({ project }: { project: AssistantProjectObservable }) => {
  const { store } = useAppStore();
  const projectDetail = buildProjectDetailViewModel({
    project,
    tasks: store.assistantTasks.all,
    followUps: store.assistantFollowUps.all,
  });

  return (
    <StyledDetailGrid>
      <AgentProjectDetailCard
        projectDetail={projectDetail}
        onCommentProject={showNotImplementedAlert}
        onOpenNextFollowUp={showNotImplementedAlert}
      />
      <AgentProjectMetaCard projectDetail={projectDetail} />
    </StyledDetailGrid>
  );
});

const StyledDetailGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StyledNotFound = styled(MDSText)`
  padding: ${({ theme }) => theme.spacing[6]} 0;
`;
